import { useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Unstable_Grid2";
import Carousel from "react-material-ui-carousel";
import { Box, Tab, Tabs } from "@mui/material";
import {
  ArrowCircleLeftOutlined,
  ArrowCircleRightOutlined,
} from "@mui/icons-material";
import { TabPanel } from "../tabPanel/TabPanel";

import { initLocalization } from "../../../utils/helpers";
import { viewMappingDetailsStrings } from "./locale";
import { CREATIVES_MEDIA_DOMAIN } from "../../../constants";

import mediaPlaceholderImage from "../../../assets/media-placeholder.jpg";
import "./style.scss";

const BUFFER_SIZE = 7;

const createChunk = (currentIndex, dataSource, offset = 0) => {
  let chunk = [];
  const prevBufferLastIndex = currentIndex - BUFFER_SIZE;
  const nextBufferLastIndex = currentIndex + BUFFER_SIZE;

  // prev buffer
  if (prevBufferLastIndex < 0) {
    chunk = [
      ...chunk,
      ...dataSource.slice(prevBufferLastIndex),
      ...dataSource.slice(0, BUFFER_SIZE + prevBufferLastIndex),
    ];
  } else {
    chunk = [...chunk, ...dataSource.slice(prevBufferLastIndex, currentIndex)];
  }

  // next buffer
  if (nextBufferLastIndex >= dataSource.length) {
    chunk = [
      ...chunk,
      ...dataSource.slice(currentIndex, nextBufferLastIndex),
      ...dataSource.slice(0, nextBufferLastIndex + 1 - dataSource.length),
    ];
  } else {
    chunk = [
      ...chunk,
      ...dataSource.slice(currentIndex, nextBufferLastIndex + 1),
    ];
  }

  // chunk offsetting
  // Note: due to the fact that the carousel's index prop doesn't work correctly
  // when the same value is passed to it, the chunk array has to be offset by
  // one item alternatively. The original array index will be kept in chunkIndex.
  chunk = JSON.parse(JSON.stringify(chunk));

  chunk.forEach((item, idx) => (item.chunkIndex = idx));

  const offsetChunk = [...chunk.slice(offset), ...chunk.slice(0, offset)];

  return offsetChunk;
};

const createMedia = (type, details) => {
  const creativeDetails = {
    ...details.creativeDetails,
    rawlocation: details.creativeDetails.rawLocation,
    rawmediaTypeName: details.creativeDetails.rawMediaTypeName,
  };

  const item = { ...details, creativeDetails };

  return (
    <>
      {item?.creativeDetails?.[`${type}mediaTypeName`].length === 0 && (
        <img
          src={mediaPlaceholderImage}
          alt={`${item.name}`}
          className="view-mappings__slide-image-placeholder"
        />
      )}
      {item?.creativeDetails?.[`${type}mediaTypeName`] === "image" && (
        <img
          src={item.creativeDetails[`${type}location`]}
          alt={`${item.name}`}
          className="view-mappings__slide-image"
        />
      )}
      {item?.creativeDetails?.[`${type}mediaTypeName`] === "video" && (
        <video
          data-testid="view-mappings__slide-video"
          className="view-mappings__slide-video"
          controls
        >
          <source src={item.creativeDetails[`${type}location`]} />
        </video>
      )}
      {item?.creativeDetails?.[`${type}mediaTypeName`]?.toLowerCase() ===
        "html5" && (
        <iframe
          className="view-mappings__slide-iframe"
          title={item?.id}
          src={
            item?.creativeDetails?.[`${type}location`]?.split(
              CREATIVES_MEDIA_DOMAIN,
            )?.[1]
          }
        />
      )}
    </>
  );
};

export const ViewMappingDetails = ({
  mappingDetailsData,
  metadataComponent,
  dataTransformer,
}) => {
  initLocalization(viewMappingDetailsStrings);

  const chunkOffset = useRef(0);

  const mappingDetailsArray = useMemo(
    () => dataTransformer(mappingDetailsData),
    [mappingDetailsData],
  );

  const useChunks = mappingDetailsArray.length > 2 * BUFFER_SIZE + 1;

  const [mappingDataChunk, setMappingDataChunk] = useState(
    useChunks ? createChunk(0, mappingDetailsArray) : mappingDetailsArray,
  );
  const [selectedTab, setSelectedTab] = useState(0);

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return (
    <Grid
      className="view-mappings"
      maxWidth="80rem"
      padding="2rem"
      margin="1rem auto"
      backgroundColor="#fff"
    >
      <Carousel
        className="view-mappings__carousel-wrapper"
        {...(useChunks && { index: BUFFER_SIZE - chunkOffset.current })}
        animation="slide"
        autoPlay={false}
        swipe={false}
        navButtonsAlwaysVisible={true}
        fullHeightHover={false}
        height="37.5rem"
        {...(useChunks && {
          onChange: (current, prev) => {
            const chunkIndex = mappingDataChunk[current].chunkIndex;

            if (
              chunkIndex + 1 >= mappingDataChunk.length ||
              chunkIndex - 1 < 0
            ) {
              const offset = 1 - chunkOffset.current;

              chunkOffset.current = offset;

              setMappingDataChunk(
                createChunk(
                  mappingDataChunk[current].index,
                  mappingDetailsArray,
                  offset,
                ),
              );
            }
          },
        })}
        navButtonsWrapperProps={{
          className: "view-mappings__nav-buttons-wrapper-props",
        }}
        navButtonsProps={{
          className: "view-mappings__nav-buttons-props",
          disabled: mappingDetailsArray?.length <= 1,
        }}
        PrevIcon={
          <Box className="view-mappings__slider-buttons view-mappings__slider-prev">
            <ArrowCircleLeftOutlined
              sx={{ width: "2.5rem", height: "2.5rem" }}
            />
            {viewMappingDetailsStrings.prevButton}
          </Box>
        }
        NextIcon={
          <Box className="view-mappings__slider-buttons view-mappings__slider-next">
            {viewMappingDetailsStrings.nextButton}
            <ArrowCircleRightOutlined
              sx={{ width: "2.5rem", height: "2.5rem" }}
            />
          </Box>
        }
        IndicatorIcon={mappingDataChunk.map(
          (item) => `${item.index + 1} / ${mappingDetailsArray.length}`,
        )}
        indicatorIconButtonProps={{
          className: "view-mappings__indicator-icon-button-props",
          disabled: true,
        }}
        activeIndicatorIconButtonProps={{
          className: "view-mappings__active-indicator-icon-button-props",
        }}
        indicatorContainerProps={{
          className: "view-mappings__indicator-container-props",
        }}
      >
        {mappingDataChunk?.map((item) => (
          <Grid
            sx={{
              border: "1px solid #ccc",
              borderRadius: "0.5rem",
              height: "100%",
            }}
            key={item.id}
          >
            <Grid container sx={{ height: "100%" }}>
              {metadataComponent({ item })}
              <Grid
                className="creative-media-wrapper"
                flex="3"
                container
                alignItems="center"
                alignContent="flex-start"
                width="100%"
                height="100%"
              >
                {item?.creativeDetails.rawMediaTypeName ? (
                  <>
                    <Box
                      sx={{
                        width: "100%",
                        mb: 1,
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Tabs
                        value={selectedTab}
                        onChange={handleChangeTab}
                        aria-label="media tabs"
                      >
                        <Tab
                          label={viewMappingDetailsStrings.preview}
                          id="tab-0"
                          aria-controls="tabpanel-0"
                        />
                        <Tab
                          label={viewMappingDetailsStrings.detail}
                          id="tab-1"
                          aria-controls="tabpanel-1"
                        />
                      </Tabs>
                    </Box>
                    <TabPanel
                      className="creative-media-tab-panel"
                      value={selectedTab}
                      index={0}
                    >
                      {createMedia("", item)}
                    </TabPanel>
                    <TabPanel
                      className="creative-media-tab-panel"
                      value={selectedTab}
                      index={1}
                    >
                      {createMedia("raw", item)}
                    </TabPanel>
                  </>
                ) : (
                  createMedia("", item)
                )}
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Carousel>
    </Grid>
  );
};

ViewMappingDetails.propTypes = {
  mappingDetailsData: PropTypes.arrayOf(PropTypes.objectOf).isRequired,
  metadataComponent: PropTypes.func,
  dataTransformer: PropTypes.func.isRequired,
};
